import { PURCHASER_API_BASE_URL, getHeaders } from "../Constants";
import Fetcher from "../Fetcher.ts";
import z from "zod";
import type { RFQFilterParams } from "../../components/RFQ/List/useRFQListFilters.tsx";
import { objectToQueryParams } from "../../util/logic.ts";
import type { AttributeType, Contact, ContactAttribute, RFQStatus, VendorAttribute, VendorRelationship } from "./RFQ.ts";

export interface VendorScores {
    rfqsSent: number;
    rfqsWithResponse: number;
    rfqsWithoutResponse: number;
    rfqsNoQuote: number;
    rfqsWithDeadline: number;
    totalQuotes: number;
    quotesOnTime: number;
    quotesComplete: number;
    totalIncomingCommunications: number;
    totalQuotedLineItems: number;
    rfqsBelowMedianPrice: number;
    rfqsWithCompetition: number;
    responseRate: number | null;
    quoteRate: number | null;
    noQuoteRate: number | null;
    onTimeRate: number | null;
    completenessRate: number | null;
    ghostRate: number | null;
    priceCompetitivenessRate: number | null;
    avgCommunicationsPerRfq: number | null;
    avgResponseTimeHours: number | null;
    compositeScore: number | null;
    letterGrade: string | null;
    confidenceLevel: "low" | "medium" | "high";
    dateRange: {
        startDate: string | null;
        endDate: string | null;
    };
}

export type NewContact = {
    email?: string;
    firstName?: string;
    lastName?: string;
};

export type MoveContactEvaluationResult = {
    action: "REASSIGN" | "COPY" | "BLOCKED";
    code: string;
    message: string;
    details: {
        sourceVendorId: number;
        targetVendorId: number;
        constraints: {
            isActive: boolean;
            hasMultiContactResponses: boolean;
            hasSharedRfqs: boolean;
            emailExistsAtTarget: boolean;
            sharedRfqIds?: number[];
            existingTargetContactId?: number;
            multiContactRfqIds?: number[];
        };
        blockingReasons?: Array<{
            code: string;
            message: string;
            metadata?: any;
        }>;
    };
    result?: {
        newContactId?: number;
    };
};

export type ContactMoveConstraints = {
    isActive: boolean;
    hasMultiContactResponses: boolean;
    hasSharedRfqs: boolean;
    emailExistsAtTarget: boolean;
    sharedRfqIds?: number[];
    existingTargetContactId?: number;
    multiContactRfqIds?: number[];
};

export type AddContactResponse = {
    id: number;
    email: string;
    vendorRelationshipId: number;
    teamEmailAddressId: number;
    existing: boolean;
};
const VendorRFQOutcomeSchema = z.enum(["QUOTES_RECEIVED", "NO_QUOTES", "NO_RESPONSE", "NO_BID"]);
export type VendorRFQOutcome = z.infer<typeof VendorRFQOutcomeSchema>;

export type VendorRFQListEntry = {
    uniquePublicId: string;
    createdAt: string;
    updatedAt: string;
    title: string | null;
    createdByUserDisplayName: string;
    createdByUserId: number;
    assignedToUserDisplayName: string;
    assignedToUserId: number;
    status: RFQStatus;
    hasUnread: boolean;
    quoteCount: number;
    messageCount: number;
    vendorCount: number;
    latestQuote?: {
        grandTotal: string | null;
        currencyCode: string | null;
    };
    outcome: VendorRFQOutcome;
    mailboxId: number | undefined;
};

export type VendorRFQListResponse = {
    results: VendorRFQListEntry[];
    pagination: {
        page: number;
        limit: number;
        total: number;
        totalPages: number;
    };
};

export type VendorRelationshipPotentialContact = {
    id: number;
    createdAt: string;
    assumedName: string | null;
    vendorRelationshipId: number;
    emailAddress: string;
    rfqs: {
        rfqUniquePublicId: string;
        lastSeen: string;
    }[];
};

export type RecentContact = {
    email: string;
    teamEmailAddressId: number;
    vendorRelationshipId: number;
    vendorName?: string | null;
};

export type VendorPerformanceSummaryResponse = {
    summary: string;
};

const f = Fetcher.default();

export default {
    getContacts: (options?: {
        searchString: string;
        page: number;
        limit: number;
        listIds?: number[];
        vendorIds?: number[];
    }): Promise<{
        data: Contact[];
        pagination: {
            page: number;
            limit: number;
            total: number;
            totalUnfiltered: number;
            totalPages: number;
        };
    }> => {
        if (!options) {
            options = {
                searchString: "",
                page: 1,
                limit: 10,
            };
        }

        let fetchUrl = `${PURCHASER_API_BASE_URL}/contacts?searchString=${encodeURIComponent(options.searchString)}&page=${options.page}&limit=${options.limit}`;

        if (options.vendorIds) {
            fetchUrl += `&vendorIds=${options.vendorIds.join(",")}`;
        }

        if (options.listIds) {
            fetchUrl += `&listIds=${options.listIds.join(",")}`;
        }

        return fetch(fetchUrl, {
            headers: getHeaders(),
        }).then(res => {
            if (!res.ok) {
                throw new Error(`Failed to fetch contacts: ${res.statusText}`);
            }
            return res.json();
        });
    },
    getRecentContacts: async (): Promise<RecentContact[]> => {
        const url = `${PURCHASER_API_BASE_URL}/contacts/recent`;
        const res = await fetch(url, { headers: getHeaders() });
        return res.json();
    },
    getContact: (contactId: number): Promise<Contact> =>
        fetch(`${PURCHASER_API_BASE_URL}/contacts/${contactId}`, {
            headers: getHeaders(),
        }).then(res => {
            if (!res.ok) {
                throw new Error(`Failed to get contact: ${res.statusText}`);
            }
            return res.json();
        }),
    addContact: async (email: string, vendorRelationshipId?: number, vendorRelationshipName?: string | null): Promise<AddContactResponse> => {
        return await f.post<AddContactResponse>("/contacts", { body: { email, vendorRelationshipId, vendorRelationshipName } });
    },
    deleteContact: (contactId: number): Promise<any> =>
        fetch(`${PURCHASER_API_BASE_URL}/contacts/${contactId}`, {
            method: "DELETE",
            headers: getHeaders(),
            body: JSON.stringify({}),
        }).then(res => res.json()),
    deleteManyContacts: (contactIds: number[]): Promise<any> =>
        fetch(`${PURCHASER_API_BASE_URL}/contacts/deleteMany`, {
            method: "POST",
            headers: getHeaders(),
            body: JSON.stringify({ contactIds }),
        }).then(res => res.json()),
    updateContact: (contactId: number, data: { email: string; firstName?: string; lastName?: string }): Promise<any> =>
        fetch(`${PURCHASER_API_BASE_URL}/contacts/${contactId}`, {
            method: "PUT",
            headers: getHeaders(),
            body: JSON.stringify(data),
        }).then(res => res.json()),
    updateContactAttributeValues: (contactId: string, data: { attributeId: number; value: string }[]): Promise<any> =>
        fetch(`${PURCHASER_API_BASE_URL}/contacts/${contactId}/attributes`, {
            headers: getHeaders(),
            method: "PUT",
            body: JSON.stringify(data),
        }).then(res => res.json()),
    getContactAttributes: (): Promise<{ attributes: ContactAttribute[] }> =>
        fetch(`${PURCHASER_API_BASE_URL}/contacts/attributes`, {
            headers: getHeaders(),
            method: "GET",
        }).then(res => res.json()),
    addContactAttribute: (contactAttribute: { type: AttributeType.STRING; name: string; description: string | null; weight: number }): any =>
        fetch(`${PURCHASER_API_BASE_URL}/contacts/attributes`, {
            method: "POST",
            headers: getHeaders(),
            body: JSON.stringify(contactAttribute),
        }),
    updateContactAttribute: (contactAttribute: {
        id: number;
        type: AttributeType.STRING;
        name: string;
        description: string | null;
        weight: number;
    }): any =>
        fetch(`${PURCHASER_API_BASE_URL}/contacts/attributes/${contactAttribute.id}`, {
            method: "PUT",
            headers: getHeaders(),
            body: JSON.stringify(contactAttribute),
        }),
    deleteContactAttribute: (contactAttributeId: number): Promise<any> =>
        fetch(`${PURCHASER_API_BASE_URL}/contacts/attributes/${contactAttributeId}`, {
            method: "DELETE",
            headers: getHeaders(),
            body: JSON.stringify({}),
        }),
    getSimulateChangeContactVendor: (contactId: number, targetVendorId: number): Promise<MoveContactEvaluationResult> =>
        fetch(`${PURCHASER_API_BASE_URL}/contacts/${contactId}/move-to-vendor/${targetVendorId}`, {
            headers: getHeaders(),
        }).then(res => res.json()),
    updateContactVendor: (contactId: number, targetVendorId: number, force?: boolean): Promise<MoveContactEvaluationResult> =>
        fetch(`${PURCHASER_API_BASE_URL}/contacts/${contactId}/move-to-vendor/${targetVendorId}`, {
            method: "POST",
            headers: getHeaders(),
            body: JSON.stringify({ force: !!force }),
        }).then(res => res.json()),
    addContactToLists: (contactId: number, listIds: number[]): Promise<any> =>
        // This is idempotent / adding a contact to a list they are already in has no effect.
        fetch(`${PURCHASER_API_BASE_URL}/contacts/${contactId}/lists`, {
            method: "PUT",
            headers: getHeaders(),
            body: JSON.stringify({ listIds }),
        }).then(res => res.json()),
    syncContactLists: (contactId: number, listIds: number[]): Promise<any> => {
        return fetch(`${PURCHASER_API_BASE_URL}/contacts/${contactId}/lists/sync`, {
            method: "POST",
            headers: getHeaders(),
            body: JSON.stringify({ listIds }),
        }).then(res => res.json());
    },

    lookupVendorRelationshipByContactEmail: (
        email: string,
    ): Promise<{
        vendorRelationships: VendorRelationship[];
    }> =>
        fetch(`${PURCHASER_API_BASE_URL}/vendor-relationships/lookup?email=${encodeURIComponent(email)}`, {
            headers: getHeaders(),
        })
            .then(res => res.json())
            .then(res => res.data),

    createVendorRelationship: (data: {
        contacts: { email: string; firstName?: string; lastName?: string }[];
        name?: string;
        domain?: string;
        attributeValues?: { attributeId: number; value: string }[];
    }): Promise<VendorRelationship> =>
        fetch(`${PURCHASER_API_BASE_URL}/vendor-relationships`, {
            method: "POST",
            headers: getHeaders(),
            body: JSON.stringify(data),
        }).then(res => res.json()),

    // Create a vendor relationship from an existing vendor (in vendor discovery).
    // If the vendor relationship already exists, the existing one will be returned.
    // The single contact id returned in the contacts array returned corresponds to the contact created from the vendor's primary contact email.
    // If the vendor has no contact email, no contact will be created, and the contacts array will be empty.
    createVendorRelationshipFromVendor: async (vendorPublicId: string): Promise<any> =>
        fetch(`${PURCHASER_API_BASE_URL}/vendor-relationships/vendor`, {
            method: "POST",
            headers: getHeaders(),
            body: JSON.stringify({ vendorPublicId }),
        }).then(res => res.json()),

    updateVendorRelationship: (vendorRelationshipId: number, data: { name?: string | null | undefined; domain?: string }): Promise<any> =>
        fetch(`${PURCHASER_API_BASE_URL}/vendor-relationships/${vendorRelationshipId}`, {
            method: "PATCH",
            headers: getHeaders(),
            body: JSON.stringify(data),
        }).then(res => res.json()),

    getVendorRelationships: ({
        searchString = "",
        page = 1,
        limit = 10,
        onlyNamed = true,
        includeAll = false,
    }): Promise<{
        data: VendorRelationship[];
        pagination: {
            page: number;
            limit: number;
            total: number;
            totalUnfiltered: number;
            totalUnnamedCount: number;
            totalPages: number;
        };
    }> =>
        fetch(
            `${PURCHASER_API_BASE_URL}/vendor-relationships?searchString=${encodeURIComponent(searchString)}&page=${page}&limit=${limit}&onlyNamed=${onlyNamed}&includeAll=${includeAll}`,
            {
                headers: getHeaders(),
            },
        ).then(res => {
            if (!res.ok) {
                throw new Error(`Failed to fetch vendor relationships: ${res.statusText}`);
            }
            return res.json();
        }),

    getVendorRelationship: (vendorRelationshipId: number, includeFiles: boolean = false): Promise<VendorRelationship> =>
        fetch(`${PURCHASER_API_BASE_URL}/vendor-relationships/${vendorRelationshipId}?includeFiles=${includeFiles}`, {
            headers: getHeaders(),
        }).then(res => res.json()),

    deleteVendorRelationship: (vendorRelationshipId: number): Promise<any> =>
        fetch(`${PURCHASER_API_BASE_URL}/vendor-relationships/${vendorRelationshipId}`, {
            method: "DELETE",
            headers: getHeaders(),
            body: JSON.stringify({}),
        }),
    deleteManyVendorRelationships: (vendorRelationshipIds: number[]): Promise<any> =>
        fetch(`${PURCHASER_API_BASE_URL}/vendor-relationships/deleteMany`, {
            method: "POST",
            headers: getHeaders(),
            body: JSON.stringify({ vendorRelationshipIds }),
        }).then(res => res.json()),

    getVendorRelationshipPotentialContacts: async (vendorRelationshipId: number): Promise<VendorRelationshipPotentialContact[]> => {
        const url = `${PURCHASER_API_BASE_URL}/vendor-relationships/${vendorRelationshipId}/potential-contacts`;
        const res = await fetch(url, { headers: getHeaders() });
        const json = await res.json();

        return json.data as VendorRelationshipPotentialContact[];
    },

    deleteVendorRelationshipPotentialContact: async (vendorRelationshipId: number, potentialContactId: number): Promise<void> => {
        const url = `${PURCHASER_API_BASE_URL}/vendor-relationships/${vendorRelationshipId}/potential-contacts/${potentialContactId}`;
        const res = await fetch(url, {
            method: "DELETE",
            headers: getHeaders(),
            body: JSON.stringify({}),
        });

        if (!res.ok || res.status > 399) {
            throw new Error("Error deleting potential contact");
        }
    },

    getVendorRelationshipContacts: ({
        vendorRelationshipId,
        searchString = "",
        page = 1,
        limit = 10,
    }: {
        vendorRelationshipId: number;
        searchString?: string;
        page?: number;
        limit?: number;
    }): Promise<{
        data: Contact[];
        pagination: {
            page: number;
            limit: number;
            total: number;
            totalUnfiltered: number;
            totalPages: number;
        };
    }> =>
        fetch(
            `${PURCHASER_API_BASE_URL}/vendor-relationships/${vendorRelationshipId}/contacts?searchString=${encodeURIComponent(searchString)}&page=${page}&limit=${limit}`,
            {
                headers: getHeaders(),
            },
        ).then(res => {
            if (!res.ok) {
                throw new Error(`Failed to fetch contacts: ${res.statusText}`);
            }
            return res.json();
        }),

    updateVendorRelationshipAttributes: (vendorRelationshipId: number, data: { [key: string]: any }): Promise<any> =>
        fetch(`${PURCHASER_API_BASE_URL}/vendor-relationships/${vendorRelationshipId}/attributes`, {
            method: "PUT",
            headers: getHeaders(),
            body: JSON.stringify(data),
        }).then(res => res.json()),

    getVendorAttributes: (): Promise<{ attributes: VendorAttribute[] }> =>
        fetch(`${PURCHASER_API_BASE_URL}/vendor-relationships/attributes`, {
            headers: getHeaders(),
            method: "GET",
        }).then(res => res.json()),
    addVendorAttribute: (contactAttribute: { type: "STRING" | "FILE"; name: string; description: string | null; weight: number }): any =>
        fetch(`${PURCHASER_API_BASE_URL}/vendor-relationships/attributes`, {
            method: "POST",
            headers: getHeaders(),
            body: JSON.stringify(contactAttribute),
        }),
    updateVendorAttribute: (contactAttribute: {
        id: number;
        type: "STRING" | "FILE";
        name: string;
        description: string | null;
        weight: number;
    }): any =>
        fetch(`${PURCHASER_API_BASE_URL}/vendor-relationships/attributes/${contactAttribute.id}`, {
            method: "PUT",
            headers: getHeaders(),
            body: JSON.stringify(contactAttribute),
        }),
    deleteVendorAttribute: (contactAttributeId: number): Promise<any> =>
        fetch(`${PURCHASER_API_BASE_URL}/vendor-relationships/attributes/${contactAttributeId}`, {
            method: "DELETE",
            headers: getHeaders(),
            body: JSON.stringify({}),
        }),
    getVendorRelationshipFiles: (vendorRelationshipId: number): Promise<any> =>
        fetch(`${PURCHASER_API_BASE_URL}/vendor-relationships/${vendorRelationshipId}/files`, {
            headers: getHeaders(),
        }).then(res => res.json()),
    createVendorRelationshipFiles: (vendorRelationshipId: number, fileIds: number[]): Promise<any> => {
        const payload = {
            files: fileIds.map(id => {
                return { fileId: id.toString() };
            }),
        };

        return fetch(`${PURCHASER_API_BASE_URL}/vendor-relationships/${vendorRelationshipId}/files`, {
            method: "POST",
            headers: getHeaders(),
            body: JSON.stringify(payload),
        }).then(res => res.json());
    },

    getVendorScores: (vendorRelationshipId: number): Promise<VendorScores> =>
        fetch(`${PURCHASER_API_BASE_URL}/vendor-relationships/${vendorRelationshipId}/scores`, {
            headers: getHeaders(),
        }).then(res => {
            if (!res.ok) {
                throw new Error(`Failed to fetch vendor scores: ${res.statusText}`);
            }
            return res.json();
        }),

    getVendorRFQList: async (
        vendorRelationshipId: number,
        filters?: RFQFilterParams & { page?: number; limit?: number },
    ): Promise<VendorRFQListResponse> => {
        const queryParams = filters ? objectToQueryParams(filters) : null;
        const queryString = queryParams ? "?" + queryParams.toString() : "";

        const url = PURCHASER_API_BASE_URL + `/vendor-relationships/${vendorRelationshipId}/rfqs` + queryString;

        const res = await fetch(url, { headers: getHeaders() });
        if (!res.ok) {
            console.error("Failed to get RFQs", res);
            throw new Error("Failed to get RFQs");
        }
        return (await res.json()) as VendorRFQListResponse;
    },

    getVendorPerformanceSummary: async (vendorRelationshipId: number): Promise<VendorPerformanceSummaryResponse> => {
        const url = `${PURCHASER_API_BASE_URL}/vendor-relationships/${vendorRelationshipId}/performance-summary`;
        const res = await fetch(url, { headers: getHeaders() });
        if (!res.ok) {
            console.error("Failed to get vendor performance summary", res);
            throw new Error("Failed to get vendor performance summary");
        }
        return (await res.json()) as VendorPerformanceSummaryResponse;
    },
};
